/**
 * Observability Adapter
 *
 * Vendor-agnostic facade for error tracking and telemetry.
 * All application code should use this adapter - never import providers directly.
 */

import type {
  ObservabilityConfig,
  ObservabilityContext,
  ObservabilityProvider,
  ObservabilityUser,
  SeverityLevel,
  SpanContext,
} from "./types"
import { noopProvider } from "./providers/noop"

let currentProvider: ObservabilityProvider = noopProvider
let isInitialized = false

/**
 * Initialize observability with the specified configuration.
 * Should be called once at application startup.
 */
export async function initObservability(
  config: ObservabilityConfig
): Promise<void> {
  if (isInitialized) {
    console.warn("[observability] Already initialized, skipping re-init")
    return
  }

  // Select provider based on config
  if (config.provider === "sentry" && config.dsn) {
    // Lazy load sentry provider to avoid bundling when not used
    const { sentryProvider } = await import("./providers/sentry")
    currentProvider = sentryProvider
  } else {
    currentProvider = noopProvider
  }

  currentProvider.init(config)
  isInitialized = true

  if (config.debug) {
    console.log("[observability] Initialized with provider:", config.provider)
  }
}

/**
 * Capture an exception.
 * Context should not contain PII - only operational data.
 */
export function captureException(
  error: Error,
  context?: ObservabilityContext
): void {
  currentProvider.captureException(error, sanitizeContext(context))
}

/**
 * Capture a message.
 */
export function captureMessage(
  message: string,
  level: SeverityLevel = "info",
  context?: ObservabilityContext
): void {
  currentProvider.captureMessage(message, level, sanitizeContext(context))
}

/**
 * Set user context.
 * Only opaque user ID allowed - no PII (email, name, etc.)
 */
export function setUser(user: ObservabilityUser | null): void {
  currentProvider.setUser(user)
}

/**
 * Set a tag for all subsequent events.
 */
export function setTag(key: string, value: string): void {
  currentProvider.setTag(key, value)
}

/**
 * Set structured context for all subsequent events.
 * Context should not contain PII.
 */
export function setContext(
  name: string,
  context: ObservabilityContext | null
): void {
  currentProvider.setContext(name, context ? sanitizeContext(context) : null)
}

/**
 * Set release version (optional, usually set at init).
 */
export function setRelease(release: string): void {
  currentProvider.setRelease?.(release)
}

/**
 * Start a span for tracing (minimal stub).
 * Returns an object with an end() method to close the span.
 */
export function startSpan(context: SpanContext): { end: () => void } {
  return currentProvider.startSpan?.(context) ?? { end: () => {} }
}

/**
 * Check if observability is initialized.
 */
export function isObservabilityInitialized(): boolean {
  return isInitialized
}

// --- PII Scrubbing ---

const PII_KEYS = new Set([
  "email",
  "password",
  "token",
  "authorization",
  "auth",
  "secret",
  "apikey",
  "api_key",
  "accesstoken",
  "access_token",
  "refreshtoken",
  "refresh_token",
  "cookie",
  "session",
  "ssn",
  "creditcard",
  "credit_card",
  "phone",
  "address",
  "name",
  "firstname",
  "first_name",
  "lastname",
  "last_name",
])

function sanitizeContext<T extends ObservabilityContext | undefined>(
  context: T
): T {
  if (!context) return context

  const sanitized: ObservabilityContext = {}

  for (const [key, value] of Object.entries(context)) {
    const lowerKey = key.toLowerCase().replace(/[-_]/g, "")
    if (PII_KEYS.has(lowerKey)) {
      sanitized[key] = "[REDACTED]"
    } else if (typeof value === "object" && value !== null) {
      sanitized[key] = sanitizeContext(value as ObservabilityContext)
    } else {
      sanitized[key] = value
    }
  }

  return sanitized as T
}
